import { env } from "src/Consts/config";
import {
  fetchAndActivate,
  getBoolean,
  getRemoteConfig,
} from "firebase/remote-config";
import {
  APP_FEATURE_REMOTE_CONFIG_KEYS,
  DEFAULT_APP_FEATURE_ACCESS,
  type AppFeatureKey,
} from "src/Consts/features";

type AppFeatureAccessConfig = Record<AppFeatureKey, boolean>;
type FeatureConfigListener = () => void;

let appFeatureAccessConfig: AppFeatureAccessConfig = {
  ...DEFAULT_APP_FEATURE_ACCESS,
};

const featureConfigListeners = new Set<FeatureConfigListener>();

const notifyFeatureConfigListeners = () => {
  featureConfigListeners.forEach((listener) => listener());
};

const readAppFeatureAccessFromRemoteConfig = (): AppFeatureAccessConfig => {
  const remoteConfig = getRemoteConfig();
  const nextConfig = { ...DEFAULT_APP_FEATURE_ACCESS };

  (Object.keys(APP_FEATURE_REMOTE_CONFIG_KEYS) as AppFeatureKey[]).forEach(
    (featureKey) => {
      const remoteConfigKey = APP_FEATURE_REMOTE_CONFIG_KEYS[featureKey];
      nextConfig[featureKey] = getBoolean(remoteConfig, remoteConfigKey);
    }
  );

  return nextConfig;
};

const refreshAppFeatureAccessConfig = () => {
  try {
    appFeatureAccessConfig = readAppFeatureAccessFromRemoteConfig();
  } catch (error) {
    console.error("Error reading app feature access from remote config", error);
  } finally {
    notifyFeatureConfigListeners();
  }
};

const getAppFeatureAccessConfig = () => appFeatureAccessConfig;

const subscribeAppFeatureAccessConfig = (listener: FeatureConfigListener) => {
  featureConfigListeners.add(listener);
  return () => {
    featureConfigListeners.delete(listener);
  };
};

const fetchRemoteConfig = async () => {
  try {
    const remoteConfig = getRemoteConfig();
    remoteConfig.defaultConfig = {
      feat_resources_enabled: true,
      ...Object.fromEntries(
        Object.entries(DEFAULT_APP_FEATURE_ACCESS).map(([featureKey, enabled]) => [
          APP_FEATURE_REMOTE_CONFIG_KEYS[featureKey as AppFeatureKey],
          enabled,
        ])
      ),
    };
    if (env.IS_LOCAL || env.IS_DEBUG) {
      remoteConfig.settings = {
        minimumFetchIntervalMillis: 0,
        fetchTimeoutMillis: 0,
      };
    }
    await fetchAndActivate(remoteConfig);
  } catch (error) {
    console.error("Error fetching remote config", error);
  } finally {
    refreshAppFeatureAccessConfig();
  }
};

const featResourcesEnabled = () => {
  const remoteConfig = getRemoteConfig();
  return getBoolean(remoteConfig, "feat_resources_enabled");
};

export { featResourcesEnabled, fetchRemoteConfig };
export { getAppFeatureAccessConfig, subscribeAppFeatureAccessConfig };
export type { AppFeatureAccessConfig };
